// UtilitySelector component - Orchestrator for utility plan selection

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { WaterPlan, InternetPlan, ElectricityPlan, SelectedService } from '@/types'
import type { UsageLevel } from '@/utils/planCalculations'
import type { ApartmentWaterChoice } from '@/stores/orderStore'
import { WaterSection } from './WaterSection'
import { InternetSection } from './InternetSection'
import { ElectricitySection } from './ElectricitySection'
import { InternetPlanModal, ElectricityPlanModal } from './modals'

interface UtilitySelectorProps {
  waterPlans?: WaterPlan[]
  internetPlans?: InternetPlan[]
  electricityPlans?: ElectricityPlan[]
  estimatedUsage?: number
  usageArray?: number[]
  usageLevel?: UsageLevel
  onUsageLevelChange?: (level: UsageLevel) => void
  squareFootage?: number
  selectedServices: SelectedService[]
  onSelectWater: (plan: WaterPlan | null) => void
  onSelectInternet: (plan: InternetPlan | null) => void
  onSelectElectricity: (plan: ElectricityPlan | null) => void
  waterAvailable?: boolean
  internetAvailable?: boolean
  electricityAvailable?: boolean
  isLoadingElectricity?: boolean
  // Apartment water management
  isApartment?: boolean
  apartmentWaterChoice: ApartmentWaterChoice
  onApartmentWaterChoiceChange: (choice: 'property-managed' | 'self-managed') => void
  // Water declined state - prevents auto-select when user explicitly removes water
  waterDeclined?: boolean
  // Show water card with unavailable message when water API returns 404
  showWaterUnavailable?: boolean
}

export function UtilitySelector({
  waterPlans = [],
  internetPlans = [],
  electricityPlans = [],
  estimatedUsage = 1000,
  usageArray,
  usageLevel = 'moderate',
  onUsageLevelChange,
  squareFootage,
  selectedServices,
  onSelectWater,
  onSelectInternet,
  onSelectElectricity,
  waterAvailable = false,
  internetAvailable = false,
  electricityAvailable = false,
  isLoadingElectricity = false,
  isApartment = false,
  apartmentWaterChoice,
  onApartmentWaterChoiceChange,
  waterDeclined = false,
  showWaterUnavailable = false,
}: UtilitySelectorProps) {
  // Track which sections are expanded
  // Auto-expand water when multiple plans exist so user must choose
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => {
      if (showWaterUnavailable) return new Set(['water'])
      return waterPlans.length > 1 ? new Set(['water', 'internet']) : new Set(['internet'])
    }
  )
  const [manuallyCollapsedSections, setManuallyCollapsedSections] = useState<Set<string>>(() => new Set())

  // Modal state for plan details
  const [internetModalPlan, setInternetModalPlan] = useState<InternetPlan | null>(null)
  const [electricityModalPlan, setElectricityModalPlan] = useState<ElectricityPlan | null>(null)

  // Get selected services
  const selectedWater = selectedServices.find(s => s.type === 'water')
  const selectedInternet = selectedServices.find(s => s.type === 'internet')
  const selectedElectricity = selectedServices.find(s => s.type === 'electricity')

  // Auto-select single water plan on mount (but not if user explicitly declined)
  useEffect(() => {
    if (waterAvailable && waterPlans.length === 1 && !selectedWater && !waterDeclined) {
      onSelectWater(waterPlans[0])
    }
  }, [waterAvailable, waterPlans, selectedWater, waterDeclined, onSelectWater])

  const shouldAutoExpandWater = waterAvailable && waterPlans.length > 1
  // Electricity plans load async, so auto-expand once they arrive (like internet)
  const shouldAutoExpandElectricity = electricityAvailable && electricityPlans.length > 0
  const visibleExpandedSections = useMemo(() => {
    const next = new Set(expandedSections)
    if ((shouldAutoExpandWater || showWaterUnavailable) && !manuallyCollapsedSections.has('water')) {
      next.add('water')
    }
    if (shouldAutoExpandElectricity && !manuallyCollapsedSections.has('electricity')) {
      next.add('electricity')
    }
    return next
  }, [expandedSections, manuallyCollapsedSections, shouldAutoExpandWater, shouldAutoExpandElectricity, showWaterUnavailable])

  const toggleSection = useCallback((section: string) => {
    const isExpanded = visibleExpandedSections.has(section)

    setExpandedSections(prev => {
      const next = new Set(prev)
      if (isExpanded) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })

    // Remember an explicit collapse so auto-expand doesn't fight the user
    setManuallyCollapsedSections(prev => {
      const next = new Set(prev)
      if (isExpanded) {
        next.add(section)
      } else {
        next.delete(section)
      }
      return next
    })
  }, [visibleExpandedSections])

  // Handle plan selection with auto-expand next section logic
  const handleSelectPlan = useCallback((
    type: 'water' | 'internet' | 'electricity',
    plan: WaterPlan | InternetPlan | ElectricityPlan | null
  ) => {
    const planId = plan ? (plan.id || plan.ID) : null

    // Check if this plan is already selected (toggle behavior)
    const currentSelection = type === 'water' ? selectedWater : type === 'internet' ? selectedInternet : selectedElectricity
    const isCurrentlySelected = plan && currentSelection?.planId === planId

    if (isCurrentlySelected) {
      // Deselect the plan
      if (type === 'water') {
        onSelectWater(null)
      } else if (type === 'internet') {
        onSelectInternet(null)
      } else {
        onSelectElectricity(null)
      }
      return
    }

    // Select the new plan
    if (type === 'water') {
      onSelectWater(plan as WaterPlan)
      setManuallyCollapsedSections(prev => {
        const next = new Set(prev)
        next.add('water')
        return next
      })
    } else if (type === 'internet') {
      onSelectInternet(plan as InternetPlan)
    } else {
      onSelectElectricity(plan as ElectricityPlan)
      setManuallyCollapsedSections(prev => {
        const next = new Set(prev)
        next.add('electricity')
        return next
      })
    }

    // Collapse current section and auto-expand next unselected utility
    setExpandedSections(prev => {
      const next = new Set(prev)
      next.delete(type)

      // Find next unselected utility and expand it
      const utilities = ['water', 'internet', 'electricity'] as const
      const currentIndex = utilities.indexOf(type)

      for (let i = currentIndex + 1; i < utilities.length; i++) {
        const nextUtility = utilities[i]
        const isNextSelected = nextUtility === 'water' ? selectedWater : nextUtility === 'internet' ? selectedInternet : selectedElectricity
        const isNextAvailable = nextUtility === 'water' ? waterAvailable : nextUtility === 'internet' ? internetAvailable : electricityAvailable

        if (isNextAvailable && !isNextSelected) {
          next.add(nextUtility)
          break
        }
      }

      return next
    })
  }, [selectedWater, selectedInternet, selectedElectricity, waterAvailable, internetAvailable, electricityAvailable, onSelectWater, onSelectInternet, onSelectElectricity])

  // Modal handlers
  const handleInternetModalSelect = useCallback(() => {
    if (internetModalPlan) {
      handleSelectPlan('internet', internetModalPlan)
      setInternetModalPlan(null)
    }
  }, [internetModalPlan, handleSelectPlan])

  const handleElectricityModalSelect = useCallback(() => {
    if (electricityModalPlan) {
      handleSelectPlan('electricity', electricityModalPlan)
      setElectricityModalPlan(null)
    }
  }, [electricityModalPlan, handleSelectPlan])

  // Handle apartment water choice - remove water from cart when property-managed
  const handleApartmentChoiceChange = useCallback((choice: 'property-managed' | 'self-managed') => {
    onApartmentWaterChoiceChange(choice)
    if (choice === 'property-managed') {
      onSelectWater(null)  // Remove water from cart
    }
  }, [onApartmentWaterChoiceChange, onSelectWater])

  return (
    <div data-single-select>
      {/* Water Service Section */}
      {(waterAvailable || showWaterUnavailable) && (
        <WaterSection
          waterPlans={waterPlans}
          selectedWater={selectedWater}
          isExpanded={visibleExpandedSections.has('water')}
          isApartment={isApartment}
          apartmentWaterChoice={apartmentWaterChoice}
          onToggle={() => toggleSection('water')}
          onSelectPlan={(plan) => handleSelectPlan('water', plan)}
          onRemove={() => onSelectWater(null)}
          onApartmentChoiceChange={handleApartmentChoiceChange}
          waterUnavailable={showWaterUnavailable}
        />
      )}

      {/* Home Internet Section */}
      <InternetSection
        internetPlans={internetPlans}
        selectedInternet={selectedInternet}
        isExpanded={visibleExpandedSections.has('internet')}
        onToggle={() => toggleSection('internet')}
        onSelectPlan={(plan) => handleSelectPlan('internet', plan)}
        onShowDetails={setInternetModalPlan}
      />

      {/* Electricity Section - only show if plans are available */}
      {electricityAvailable && (
        <ElectricitySection
          electricityPlans={electricityPlans}
          selectedElectricity={selectedElectricity}
          isExpanded={visibleExpandedSections.has('electricity')}
          estimatedUsage={estimatedUsage}
          usageArray={usageArray}
          usageLevel={usageLevel}
          squareFootage={squareFootage}
          isLoadingElectricity={isLoadingElectricity}
          onToggle={() => toggleSection('electricity')}
          onSelectPlan={(plan) => handleSelectPlan('electricity', plan)}
          onShowDetails={setElectricityModalPlan}
          onUsageLevelChange={onUsageLevelChange}
        />
      )}

      {/* No utilities available */}
      {!waterAvailable && !showWaterUnavailable && internetPlans.length === 0 && electricityPlans.length === 0 && (
        <div className="utility-selector__empty">
          <p>No utility services are currently available for this address.</p>
        </div>
      )}

      {/* Internet Plan Details Modal */}
      {internetModalPlan && (
        <InternetPlanModal
          plan={internetModalPlan}
          onClose={() => setInternetModalPlan(null)}
          onSelect={handleInternetModalSelect}
          isSelected={selectedInternet?.planId === (internetModalPlan.id || internetModalPlan.ID)}
        />
      )}

      {/* Electricity Plan Details Modal */}
      {electricityModalPlan && (
        <ElectricityPlanModal
          plan={electricityModalPlan}
          onClose={() => setElectricityModalPlan(null)}
          onSelect={handleElectricityModalSelect}
          isSelected={selectedElectricity?.planId === (electricityModalPlan.id || electricityModalPlan.ID)}
        />
      )}
    </div>
  )
}
